import { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Database, AlertTriangle, Download, Upload, Users, UserPlus, Trash2, Send, Wallet, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';

const API = process.env.REACT_APP_BACKEND_URL;
const ic = "w-full rounded-md border border-[#E4DFD3] bg-white px-3 py-2 text-sm text-[#1A2620] placeholder-[#5C6B63] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]";

const Settings = () => {
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetUrls, setSheetUrls] = useState({ yield_url: '', sale_url: '', expense_url: '' });
  const [uploading, setUploading] = useState({});
  const [uploadResults, setUploadResults] = useState({});

  // Staff & workspace state
  const [members, setMembers] = useState([]);
  const [newStaff, setNewStaff] = useState({ name: '', email: '', phone: '', password: '', role: 'staff' });
  const [addingStaff, setAddingStaff] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);

  // Transfers state
  const [transfers, setTransfers] = useState([]);
  const [newTransfer, setNewTransfer] = useState({ from_user_id: '', to_user_id: '', amount: '', note: '' });
  const [sendingTransfer, setSendingTransfer] = useState(false);
  const [editTransfer, setEditTransfer] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchMembers();
    fetchTransfers();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/api/sync/status`, { withCredentials: true });
      setDbStats(data);
    } catch {} finally { setLoading(false); }
  };
  const fetchMembers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/workspace/members`, { withCredentials: true });
      setMembers(data || []);
    } catch {}
  };
  const fetchTransfers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/transfers`, { withCredentials: true });
      setTransfers(data || []);
    } catch {}
  };

  const handleSync = async () => {
    if (syncing) return;
    if (!sheetUrls.yield_url.trim() && !sheetUrls.sale_url.trim() && !sheetUrls.expense_url.trim()) {
      toast.error('Paste at least one Google Sheet URL first'); return;
    }
    setSyncing(true); setSyncResult(null); toast.info('Syncing...');
    try {
      const { data } = await axios.post(`${API}/api/sync/sheets`, {
        yield_url: sheetUrls.yield_url.trim(),
        sale_url: sheetUrls.sale_url.trim(),
        expense_url: sheetUrls.expense_url.trim(),
      }, { withCredentials: true });
      setSyncResult(data); fetchStats();
      // Clear URLs after sync so user must re-enter next time
      setSheetUrls({ yield_url: '', sale_url: '', expense_url: '' });
      const t = (data.new_bags || 0) + (data.new_harvests || 0) + (data.new_parties || 0) + (data.new_sales || 0) + (data.new_expenses || 0);
      t > 0 ? toast.success(`Added ${t} records`) : toast.info('Up to date');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSyncing(false); }
  };

  const downloadTemplate = async (entity) => {
    try {
      const resp = await axios.get(`${API}/api/templates/${entity}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entity}_template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${entity}_template.csv downloaded`);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (entity, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [entity]: true }));
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/bulk-upload/${entity}`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResults(r => ({ ...r, [entity]: data }));
      toast.success(`${data.added} ${entity} added, ${data.skipped} skipped`);
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(u => ({ ...u, [entity]: false })); }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!newStaff.name || !newStaff.email || !newStaff.password) {
      toast.error('Name, email and password are required'); return;
    }
    setAddingStaff(true);
    try {
      await axios.post(`${API}/api/auth/staff`, newStaff, { withCredentials: true });
      toast.success(`${newStaff.role === 'admin' ? 'Admin' : 'Staff'} "${newStaff.name}" added`);
      setNewStaff({ name: '', email: '', phone: '', password: '', role: 'staff' });
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team member');
    } finally { setAddingStaff(false); }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      await axios.put(`${API}/api/auth/workspace/staff/${userId}/role`, { role }, { withCredentials: true });
      toast.success(`Role updated to ${role}`);
      setEditingRoleId(null);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleRemoveStaff = async (userId, name) => {
    if (!window.confirm(`Remove "${name}" from the workspace? They will lose access immediately.`)) return;
    try {
      await axios.delete(`${API}/api/auth/workspace/staff/${userId}`, { withCredentials: true });
      toast.success('Member removed');
      fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    const amt = parseFloat(newTransfer.amount);
    if (!newTransfer.from_user_id || !newTransfer.to_user_id || !amt || amt <= 0) {
      toast.error('From, To and a valid amount are required'); return;
    }
    if (newTransfer.from_user_id === newTransfer.to_user_id) {
      toast.error('From and To must be different'); return;
    }
    setSendingTransfer(true);
    try {
      await axios.post(`${API}/api/transfers`, {
        from_user_id: newTransfer.from_user_id,
        to_user_id: newTransfer.to_user_id,
        amount: amt,
        note: newTransfer.note,
      }, { withCredentials: true });
      toast.success('Transfer recorded');
      setNewTransfer({ from_user_id: '', to_user_id: '', amount: '', note: '' });
      fetchTransfers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSendingTransfer(false); }
  };

  const handleUpdateTransfer = async (e) => {
    e.preventDefault();
    if (!editTransfer) return;
    const amt = parseFloat(editTransfer.amount);
    if (!editTransfer.from_user_id || !editTransfer.to_user_id || !amt || amt <= 0) {
      toast.error('From, To and a valid amount are required'); return;
    }
    if (editTransfer.from_user_id === editTransfer.to_user_id) {
      toast.error('From and To must be different'); return;
    }
    try {
      await axios.put(`${API}/api/transfers/${editTransfer.id}`, {
        from_user_id: editTransfer.from_user_id,
        to_user_id: editTransfer.to_user_id,
        amount: amt,
        note: editTransfer.note || '',
      }, { withCredentials: true });
      toast.success('Transfer updated');
      setEditTransfer(null);
      fetchTransfers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteTransfer = async (t) => {
    if (!window.confirm(`Delete transfer of Rs.${t.amount.toFixed(0)} from ${t.from_user_name} → ${t.to_user_name}?`)) return;
    try {
      await axios.delete(`${API}/api/transfers/${t.id}`, { withCredentials: true });
      toast.success('Transfer deleted');
      fetchTransfers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  if (user?.role !== 'admin') {
    // Staff see a limited Settings page (just their profile / transfers)
    return (
      <div className="space-y-6" data-testid="settings-staff-page">
        <div><h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-[#1A2620] font-['Manrope']">Settings</h1><p className="text-[#5C6B63] mt-2">Send money and view workspace transfers</p></div>

        {/* Money Transfers (staff) */}
        <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-4 sm:p-6" data-testid="transfers-section">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center flex-shrink-0"><Wallet className="w-5 h-5 text-white" /></div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-[#1A2620] font-['Manrope']">Money Transfers</h2>
              <p className="text-sm text-[#5C6B63]">Record money sent to or received from workspace members.</p>
            </div>
          </div>
          <form onSubmit={handleTransfer} className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-4">
            <select value={newTransfer.from_user_id} onChange={e => setNewTransfer({ ...newTransfer, from_user_id: e.target.value })} className={ic} data-testid="transfer-sender-staff">
              <option value="">From</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
            </select>
            <select value={newTransfer.to_user_id} onChange={e => setNewTransfer({ ...newTransfer, to_user_id: e.target.value })} className={ic} data-testid="transfer-recipient">
              <option value="">To</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
            </select>
            <input type="number" step="0.01" value={newTransfer.amount} onChange={e => setNewTransfer({ ...newTransfer, amount: e.target.value })} className={ic} placeholder="Amount" data-testid="transfer-amount" />
            <input value={newTransfer.note} onChange={e => setNewTransfer({ ...newTransfer, note: e.target.value })} className={ic} placeholder="Note (optional)" data-testid="transfer-note" />
            <button type="submit" disabled={sendingTransfer} className="bg-[#2C4A3B] text-white rounded-md px-4 py-2 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="transfer-submit"><Send className="w-4 h-4" />{sendingTransfer ? 'Sending...' : 'Record'}</button>
          </form>
          <TransfersList transfers={transfers} currentUserId={user.id} canEdit={true} onEdit={setEditTransfer} onDelete={handleDeleteTransfer} />
        </div>

        {/* Edit Transfer Modal (staff) */}
        <Modal show={!!editTransfer} onClose={() => setEditTransfer(null)} title="Edit Transfer" testId="edit-transfer-modal-staff">
          {editTransfer && (
            <form onSubmit={handleUpdateTransfer} className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-1">From</label>
                <select value={editTransfer.from_user_id} onChange={e => setEditTransfer({ ...editTransfer, from_user_id: e.target.value })} className={ic}>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-1">To</label>
                <select value={editTransfer.to_user_id} onChange={e => setEditTransfer({ ...editTransfer, to_user_id: e.target.value })} className={ic}>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-1">Amount</label>
                <input type="number" step="0.01" value={editTransfer.amount} onChange={e => setEditTransfer({ ...editTransfer, amount: e.target.value })} className={ic} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-1">Note</label>
                <input value={editTransfer.note || ''} onChange={e => setEditTransfer({ ...editTransfer, note: e.target.value })} className={ic} />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 bg-[#2C4A3B] text-white rounded-md px-4 py-2 font-medium">Save</button>
                <button type="button" onClick={() => setEditTransfer(null)} className="flex-1 border border-[#E4DFD3] text-[#2C4A3B] rounded-md px-4 py-2 font-medium">Cancel</button>
              </div>
            </form>
          )}
        </Modal>
      </div>
    );
  }

  const entities = [
    { key: 'bags', label: 'Bags', desc: 'bag_id, variety, weight, location, action_status, date_spawning' },
    { key: 'parties', label: 'Parties', desc: 'name, phone, quantity_per_packet, price_per_packet, payment_method' },
    { key: 'sales', label: 'Sales', desc: 'party_name, num_packets, payment_type, sale_date' },
    { key: 'expenses', label: 'Expenses', desc: 'expense_name, expense_amount, narration, expense_date' },
  ];

  return (<div className="space-y-6" data-testid="settings-page">
    <div><h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#1A2620] font-['Manrope']">Settings & Import</h1><p className="text-[#5C6B63] mt-2">Manage your workspace, team, and data</p></div>

    {/* DB Stats */}
    {!loading && dbStats && <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-testid="db-stats">
      <div className="bg-white border border-[#E4DFD3] rounded-lg p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B63] mb-1">Bags</p><p className="text-2xl font-bold text-[#1A2620] font-['IBM_Plex_Sans']">{dbStats.bags}</p></div>
      <div className="bg-white border border-[#E4DFD3] rounded-lg p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B63] mb-1">Harvests</p><p className="text-2xl font-bold text-[#1A2620] font-['IBM_Plex_Sans']">{dbStats.harvests}</p></div>
      <div className="bg-white border border-[#E4DFD3] rounded-lg p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B63] mb-1">Parties</p><p className="text-2xl font-bold text-[#1A2620] font-['IBM_Plex_Sans']">{dbStats.parties}</p></div>
      <div className="bg-white border border-[#E4DFD3] rounded-lg p-4"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5C6B63] mb-1">Sales</p><p className="text-2xl font-bold text-[#1A2620] font-['IBM_Plex_Sans']">{dbStats.sales}</p></div>
    </div>}

    {/* Staff Management */}
    <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-4 sm:p-6" data-testid="staff-section">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center flex-shrink-0"><Users className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-[#1A2620] font-['Manrope']">Team Members</h2>
          <p className="text-sm text-[#5C6B63]">Add staff to your private workspace. They can log in and record data under your workspace.</p>
        </div>
      </div>

      <form onSubmit={handleAddStaff} className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-5" data-testid="add-staff-form">
        <input value={newStaff.name} onChange={e => setNewStaff({ ...newStaff, name: e.target.value })} className={ic} placeholder="Full name" data-testid="staff-name-input" />
        <input type="email" value={newStaff.email} onChange={e => setNewStaff({ ...newStaff, email: e.target.value })} className={ic} placeholder="email@example.com" data-testid="staff-email-input" />
        <input type="tel" value={newStaff.phone} onChange={e => setNewStaff({ ...newStaff, phone: e.target.value })} className={ic} placeholder="Mobile (optional)" data-testid="staff-phone-input" />
        <select value={newStaff.role} onChange={e => setNewStaff({ ...newStaff, role: e.target.value })} className={ic} data-testid="staff-role-select">
          <option value="staff">Staff</option>
          <option value="admin">Admin</option>
        </select>
        <input type="password" value={newStaff.password} onChange={e => setNewStaff({ ...newStaff, password: e.target.value })} className={ic} placeholder="Temporary password" minLength={6} data-testid="staff-password-input" />
        <button type="submit" disabled={addingStaff} className="bg-[#2C4A3B] text-white rounded-md px-4 py-2 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="add-staff-btn"><UserPlus className="w-4 h-4" />{addingStaff ? 'Adding...' : 'Add'}</button>
      </form>

      <div className="overflow-x-auto border border-[#E4DFD3] rounded-lg">
        <table className="w-full">
          <thead><tr className="bg-[#F7F5F0] border-b border-[#E4DFD3] text-left text-xs font-semibold uppercase tracking-wider text-[#5C6B63]">
            <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Role</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody data-testid="members-list">
            {members.length === 0 ? <tr><td colSpan="5" className="p-4 text-center text-sm text-[#5C6B63]">No members yet.</td></tr> : members.map(m => (
              <tr key={m.id} className="border-b border-[#E4DFD3] last:border-0" data-testid={`member-row-${m.id}`}>
                <td className="p-3 text-sm font-medium text-[#1A2620]">{m.name} {m.id === user.id && <span className="text-xs text-[#5C6B63]">(you)</span>}</td>
                <td className="p-3 text-sm text-[#1A2620]">{m.email}</td>
                <td className="p-3 text-sm text-[#1A2620]">{m.phone || '—'}</td>
                <td className="p-3 text-sm">
                  {editingRoleId === m.id ? (
                    <select defaultValue={m.role} onChange={e => handleChangeRole(m.id, e.target.value)} onBlur={() => setEditingRoleId(null)} autoFocus className="rounded border border-[#E4DFD3] bg-white px-2 py-1 text-xs" data-testid={`role-select-${m.id}`}>
                      <option value="staff">staff</option>
                      <option value="admin">admin</option>
                    </select>
                  ) : (
                    <button onClick={() => setEditingRoleId(m.id)} className={`inline-block px-2 py-0.5 rounded text-xs font-semibold hover:opacity-80 ${m.role === 'admin' ? 'bg-[#2C4A3B] text-white' : 'bg-[#EDF5EE] text-[#3A7D44]'}`} data-testid={`role-badge-${m.id}`} title="Click to change role">{m.role}</button>
                  )}
                </td>
                <td className="p-3 text-right">
                  {m.id !== user.id && (
                    <button onClick={() => handleRemoveStaff(m.id, m.name)} className="text-[#D05A4A] hover:bg-[#FAEDED] rounded p-1.5" data-testid={`remove-staff-${m.id}`} title="Remove"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Money Transfers */}
    <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-4 sm:p-6" data-testid="transfers-section">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center flex-shrink-0"><Wallet className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-[#1A2620] font-['Manrope']">Money Transfers</h2>
          <p className="text-sm text-[#5C6B63]">Record money sent between workspace members.</p>
        </div>
      </div>
      <form onSubmit={handleTransfer} className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-4" data-testid="transfer-form">
        <select value={newTransfer.from_user_id} onChange={e => setNewTransfer({ ...newTransfer, from_user_id: e.target.value })} className={ic} data-testid="transfer-sender">
          <option value="">From</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
        </select>
        <select value={newTransfer.to_user_id} onChange={e => setNewTransfer({ ...newTransfer, to_user_id: e.target.value })} className={ic} data-testid="transfer-recipient">
          <option value="">To</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
        </select>
        <input type="number" step="0.01" value={newTransfer.amount} onChange={e => setNewTransfer({ ...newTransfer, amount: e.target.value })} className={ic} placeholder="Amount" data-testid="transfer-amount" />
        <input value={newTransfer.note} onChange={e => setNewTransfer({ ...newTransfer, note: e.target.value })} className={ic} placeholder="Note (optional)" data-testid="transfer-note" />
        <button type="submit" disabled={sendingTransfer} className="bg-[#2C4A3B] text-white rounded-md px-4 py-2 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="transfer-submit"><Send className="w-4 h-4" />{sendingTransfer ? 'Sending...' : 'Record'}</button>
      </form>
      <TransfersList transfers={transfers} currentUserId={user.id} canEdit={true} onEdit={setEditTransfer} onDelete={handleDeleteTransfer} />
    </div>

    {/* Google Sheets Config */}
    <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-4 sm:p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center flex-shrink-0"><Database className="w-5 h-5 text-white" /></div>
        <div><h2 className="text-lg sm:text-xl font-bold text-[#1A2620] font-['Manrope']">Google Sheets Sync</h2><p className="text-sm text-[#5C6B63]">Paste your Google Sheet URLs each time you sync. URLs are never saved — use them once and run sync.</p></div>
      </div>
      <div className="space-y-3 mb-4">
        <div><label className="block text-sm font-semibold text-[#1A2620] mb-1">Yield/Harvest Sheet URL</label><input value={sheetUrls.yield_url} onChange={e => setSheetUrls({ ...sheetUrls, yield_url: e.target.value })} className={ic} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="yield-sheet-url" /></div>
        <div><label className="block text-sm font-semibold text-[#1A2620] mb-1">Sale Data Sheet URL</label><input value={sheetUrls.sale_url} onChange={e => setSheetUrls({ ...sheetUrls, sale_url: e.target.value })} className={ic} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="sale-sheet-url" /></div>
        <div><label className="block text-sm font-semibold text-[#1A2620] mb-1">Expenses Sheet URL</label><input value={sheetUrls.expense_url} onChange={e => setSheetUrls({ ...sheetUrls, expense_url: e.target.value })} className={ic} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="expense-sheet-url" /></div>
      </div>
      <div>
        <button onClick={handleSync} disabled={syncing} className="w-full sm:w-auto bg-[#2C4A3B] text-white rounded-md px-4 py-2 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="sync-sheets-button"><RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing...' : 'Sync Now'}</button>
      </div>
    </div>

    {syncResult && <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-4 sm:p-6" data-testid="sync-results">
      <h3 className="text-lg font-semibold text-[#1A2620] font-['Manrope'] mb-3">Sync Results</h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="p-3 bg-[#EDF5EE] rounded-lg"><p className="text-xs font-semibold text-[#5C6B63]">New Bags</p><p className="text-xl font-bold text-[#3A7D44]">{syncResult.new_bags || 0}</p></div>
        <div className="p-3 bg-[#EDF5EE] rounded-lg"><p className="text-xs font-semibold text-[#5C6B63]">New Harvests</p><p className="text-xl font-bold text-[#3A7D44]">{syncResult.new_harvests || 0}</p></div>
        <div className="p-3 bg-[#EDF5EE] rounded-lg"><p className="text-xs font-semibold text-[#5C6B63]">New Parties</p><p className="text-xl font-bold text-[#3A7D44]">{syncResult.new_parties || 0}</p></div>
        <div className="p-3 bg-[#EDF5EE] rounded-lg"><p className="text-xs font-semibold text-[#5C6B63]">New Sales</p><p className="text-xl font-bold text-[#3A7D44]">{syncResult.new_sales || 0}</p></div>
        <div className="p-3 bg-[#EDF5EE] rounded-lg"><p className="text-xs font-semibold text-[#5C6B63]">New Expenses</p><p className="text-xl font-bold text-[#3A7D44]">{syncResult.new_expenses || 0}</p></div>
      </div>
      {(syncResult.yield_error || syncResult.sale_error || syncResult.expense_error) && <div className="mt-3 p-3 bg-[#FAEDED] rounded-lg flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-[#D05A4A] flex-shrink-0 mt-0.5" /><div className="text-xs text-[#D05A4A]">{syncResult.yield_error && <p>{syncResult.yield_error}</p>}{syncResult.sale_error && <p>{syncResult.sale_error}</p>}{syncResult.expense_error && <p>{syncResult.expense_error}</p>}</div></div>}
    </div>}

    <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-4 sm:p-6">
      <h2 className="text-lg sm:text-xl font-bold text-[#1A2620] font-['Manrope'] mb-4">CSV Bulk Upload</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {entities.map(ent => (
          <div key={ent.key} className="border border-[#E4DFD3] rounded-lg p-4">
            <h3 className="font-semibold text-[#1A2620] mb-1">{ent.label}</h3>
            <p className="text-xs text-[#5C6B63] mb-3">{ent.desc}</p>
            <div className="flex flex-col gap-2">
              <button onClick={() => downloadTemplate(ent.key)} className="w-full border border-[#E4DFD3] text-[#2C4A3B] rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2" data-testid={`template-${ent.key}`}><Download className="w-3.5 h-3.5" /> Download Template</button>
              <label className={`w-full bg-[#2C4A3B] text-white rounded-md px-3 py-2 text-sm font-medium flex items-center justify-center gap-2 cursor-pointer ${uploading[ent.key] ? 'opacity-50' : 'hover:bg-[#3E6652]'}`} data-testid={`upload-${ent.key}`}>
                <Upload className="w-3.5 h-3.5" /> {uploading[ent.key] ? 'Uploading...' : 'Upload CSV'}
                <input type="file" accept=".csv" className="hidden" disabled={uploading[ent.key]} onChange={e => { handleFileUpload(ent.key, e.target.files[0]); e.target.value = ''; }} />
              </label>
            </div>
            {uploadResults[ent.key] && <div className="mt-2 p-2 bg-[#EDF5EE] rounded text-xs text-[#3A7D44]">Added: {uploadResults[ent.key].added}, Skipped: {uploadResults[ent.key].skipped}{uploadResults[ent.key].errors?.length > 0 && <div className="text-[#D05A4A] mt-1">{uploadResults[ent.key].errors.join(', ')}</div>}</div>}
          </div>
        ))}
      </div>
    </div>

    {/* Edit Transfer Modal */}
    <Modal show={!!editTransfer} onClose={() => setEditTransfer(null)} title="Edit Transfer" testId="edit-transfer-modal">
      {editTransfer && (
        <form onSubmit={handleUpdateTransfer} className="space-y-3">
          <div>
            <label className="block text-sm font-semibold text-[#1A2620] mb-1">From</label>
            <select value={editTransfer.from_user_id} onChange={e => setEditTransfer({ ...editTransfer, from_user_id: e.target.value })} className={ic} data-testid="edit-transfer-from">
              {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#1A2620] mb-1">To</label>
            <select value={editTransfer.to_user_id} onChange={e => setEditTransfer({ ...editTransfer, to_user_id: e.target.value })} className={ic} data-testid="edit-transfer-to">
              {members.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#1A2620] mb-1">Amount</label>
            <input type="number" step="0.01" value={editTransfer.amount} onChange={e => setEditTransfer({ ...editTransfer, amount: e.target.value })} className={ic} data-testid="edit-transfer-amount" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#1A2620] mb-1">Note</label>
            <input value={editTransfer.note || ''} onChange={e => setEditTransfer({ ...editTransfer, note: e.target.value })} className={ic} data-testid="edit-transfer-note" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" className="flex-1 bg-[#2C4A3B] text-white rounded-md px-4 py-2 font-medium" data-testid="save-transfer-edit">Save</button>
            <button type="button" onClick={() => setEditTransfer(null)} className="flex-1 border border-[#E4DFD3] text-[#2C4A3B] rounded-md px-4 py-2 font-medium">Cancel</button>
          </div>
        </form>
      )}
    </Modal>
  </div>);
};

const TransfersList = ({ transfers, currentUserId, canEdit = false, onEdit = null, onDelete = null }) => (
  <div className="overflow-x-auto border border-[#E4DFD3] rounded-lg">
    <table className="w-full">
      <thead><tr className="bg-[#F7F5F0] border-b border-[#E4DFD3] text-left text-xs font-semibold uppercase tracking-wider text-[#5C6B63]">
        <th className="p-3">Date</th><th className="p-3">From</th><th className="p-3">To</th><th className="p-3">Amount</th><th className="p-3">Note</th>{canEdit && <th className="p-3 text-right">Actions</th>}
      </tr></thead>
      <tbody data-testid="transfers-list">
        {transfers.length === 0 ? <tr><td colSpan={canEdit ? 6 : 5} className="p-4 text-center text-sm text-[#5C6B63]">No transfers yet.</td></tr> : transfers.map(t => {
          const isOutgoing = t.from_user_id === currentUserId;
          return (
            <tr key={t.id} className="border-b border-[#E4DFD3] last:border-0" data-testid={`transfer-row-${t.id}`}>
              <td className="p-3 text-sm text-[#1A2620] font-['IBM_Plex_Sans']">{new Date(t.created_at).toLocaleString()}</td>
              <td className="p-3 text-sm text-[#1A2620]">{t.from_user_name}</td>
              <td className="p-3 text-sm text-[#1A2620]">{t.to_user_name}</td>
              <td className={`p-3 text-sm font-semibold font-['IBM_Plex_Sans'] ${isOutgoing ? 'text-[#D05A4A]' : 'text-[#3A7D44]'}`}>
                {isOutgoing ? '-' : '+'}Rs.{t.amount.toFixed(0)}
              </td>
              <td className="p-3 text-sm text-[#5C6B63]">{t.note || '—'}</td>
              {canEdit && (
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => onEdit && onEdit({ ...t })} className="p-1.5 hover:bg-[#F7F5F0] rounded" data-testid={`edit-transfer-${t.id}`} title="Edit"><Pencil className="w-3.5 h-3.5 text-[#2C4A3B]" /></button>
                    <button onClick={() => onDelete && onDelete(t)} className="p-1.5 hover:bg-[#FAEDED] rounded" data-testid={`delete-transfer-${t.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5 text-[#D05A4A]" /></button>
                  </div>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default Settings;
